
import React, {
    useEffect,
    useRef,
    useState
} from "react";

import useStore from "../Store";
import ToDoApi from "../API/ToDoApi";
import Camera from "../Camera/camera";

import TaskFilters from "./components/TaskFilters";
import TaskList from "./components/TaskList";
import NewTaskModal from "./components/NewTaskModal";
import CompleteTaskModal from "./components/CompleteTaskModal";
import PhotoLightbox from "./components/PhotoLightbox";
import TaskDetailsModal from "./components/TaskDetailsModal";

import {
    MainDiv,
    Header,
    Title,
    Button
} from "./design";

// Текущая дата
function today() {
    const d = new Date();

    return [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, "0"),
        String(d.getDate()).padStart(2, "0")
    ].join("-");
}

// Сжатие фотографий перед отправкой
function compressImage(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () =>
            reject(new Error("Datei konnte nicht gelesen werden"));

        reader.onload = () => {
            const image = new Image();

            image.onerror = () =>
                reject(new Error("Bild konnte nicht geöffnet werden"));

            image.onload = () => {
                const scale = Math.min(
                    1,
                    1200 / Math.max(image.width, image.height)
                );

                const canvas = document.createElement("canvas");

                canvas.width = Math.max(
                    1,
                    Math.round(image.width * scale)
                );

                canvas.height = Math.max(
                    1,
                    Math.round(image.height * scale)
                );

                canvas.getContext("2d").drawImage(
                    image,
                    0,
                    0,
                    canvas.width,
                    canvas.height
                );

                canvas.toBlob(
                    blob => {
                        if (blob) resolve(blob);
                        else reject(new Error("Foto-Komprimierung fehlgeschlagen"));
                    },
                    "image/jpeg",
                    0.8
                );
            };

            image.src = reader.result;
        };

        reader.readAsDataURL(file);
    });
}

const emptyForm = (house = "") => ({
    house,
    floor: "",
    comment: "",
    author: "",
    date: today()
});

const ToDo = () => {
    const houses = useStore(
        state => state.Houses.Location1
    ) ?? [];

    const Floors = useStore(
        state => state.Floors
    ) ?? [];

    const [tasks, setTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);

    const [selectedHouse, setSelectedHouse] = useState("");
    const [selectedFloor, setSelectedFloor] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(() => emptyForm());

    const [completionTask, setCompletionTask] = useState(null);
    const [completionComment, setCompletionComment] = useState("");

    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraTarget, setCameraTarget] = useState("create");

    const [previewPhoto, setPreviewPhoto] = useState(null);

    const [createPhotos, setCreatePhotos] = useState([]);
    const [completionPhotos, setCompletionPhotos] = useState([]);
    const [photoProcessing, setPhotoProcessing] = useState(false);

    const createRef = useRef([]);
    const completionRef = useRef([]);
    const photoBusyRef = useRef(false);
    const photoUrlsRef = useRef(new Set());

    // Освобождение памяти при закрытии компонента
    useEffect(() => {
        return () => {
            photoUrlsRef.current.forEach(url =>
                URL.revokeObjectURL(url)
            );
            photoUrlsRef.current.clear();
        };
    }, []);

    // Обновление массива фотографий
    function setPhotoTarget(target, photos) {
        if (target === "create") {
            createRef.current = photos;
            setCreatePhotos(photos);
        } else {
            completionRef.current = photos;
            setCompletionPhotos(photos);
        }
    }

    function clearPhotos(target) {
        const photos = target === "create"
            ? createRef.current
            : completionRef.current;

        photos.forEach(photo => {
            URL.revokeObjectURL(photo.preview);
            photoUrlsRef.current.delete(photo.preview);
        });

        setPhotoTarget(target, []);
    }

    function removePhoto(target, index) {
        if (photoBusyRef.current) return;

        const photos = target === "create"
            ? createRef.current
            : completionRef.current;

        const removed = photos[index];

        if (!removed) return;

        URL.revokeObjectURL(removed.preview);
        photoUrlsRef.current.delete(removed.preview);

        setPhotoTarget(
            target,
            photos.filter((_, i) => i !== index)
        );
    }

    // Добавление нескольких фотографий
    async function addPhotos(files, target) {
        const selected = Array.from(files ?? []);

        if (!selected.length || photoBusyRef.current) return;

        const current = target === "create"
            ? createRef.current
            : completionRef.current;

        if (current.length + selected.length > 12) {
            throw new Error("Maximal 12 Fotos pro Anfrage.");
        }

        if (selected.some(file => !file.type.startsWith("image/"))) {
            throw new Error("Bitte nur Bilder auswählen.");
        }

        photoBusyRef.current = true;
        setPhotoProcessing(true);

        try {
            const blobs = await Promise.all(
                selected.map(compressImage)
            );

            const additions = blobs.map(blob => {
                const preview = URL.createObjectURL(blob);

                photoUrlsRef.current.add(preview);

                return {
                    blob,
                    preview
                };
            });

            const latest = target === "create"
                ? createRef.current
                : completionRef.current;

            setPhotoTarget(target, [
                ...latest,
                ...additions
            ]);

        } finally {
            photoBusyRef.current = false;
            setPhotoProcessing(false);
        }
    }

    async function handleFiles(files, target) {
        try {
            await addPhotos(files, target);
        } catch (err) {
            alert(err.message);
        }
    }

    // Загрузка заданий с сервера
    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError("");


        ToDoApi.getTasks(
            selectedHouse,
            selectedFloor,
            statusFilter,
            controller.signal
        )
            .then(result => {
                if (!controller.signal.aborted) {
                    setTasks(result.Tasks ?? []);
                }
            })
            .catch(err => {
                if (!controller.signal.aborted) {
                    setError(err.message);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();

    }, [selectedHouse, statusFilter, selectedFloor]);

    async function reloadTasks() {
        const result = await ToDoApi.getTasks(
            selectedHouse,
            selectedFloor,
            statusFilter
        );

        setTasks(result.Tasks ?? []);
    }

    // Камера
    function openCamera(target) {
        setCameraTarget(target);
        setCameraOpen(true);
    }

    function receiveCameraPhoto(blob) {
        if (!blob) return;

        const file = new File(
            [blob],
            "camera.png",
            { type: blob.type || "image/png" }
        );

        void handleFiles([file], cameraTarget);
    }

    // Создание задания
    function openNewTask() {
        clearPhotos("create");

        setForm(emptyForm(
            selectedHouse || houses[0] || ""
        ));

        setModalOpen(true);
    }

    function closeNewTask() {
        if (saving || photoProcessing) return;

        clearPhotos("create");
        setModalOpen(false);
    }

    async function saveTask(event) {
        event.preventDefault();

        if (
            saving ||
            photoProcessing ||
            !createPhotos.length ||
            !form.house ||
            // !form.floor ||
            !form.comment.trim() ||
            !form.author.trim() ||
            !form.date
        ) return;

        setSaving(true);

        try {
            await ToDoApi.addTask(
                {
                    ...form,
                    comment: form.comment.trim(),
                    author: form.author.trim()
                },
                createPhotos
            );

            clearPhotos("create");
            setModalOpen(false);

            await reloadTasks();

        } catch (err) {
            alert("Fehler beim Speichern: " + err.message);
        } finally {
            setSaving(false);
        }
    }

    // Выполнение задания
    function closeCompletion() {
        if (saving || photoProcessing) return;

        clearPhotos("completion");
        setCompletionTask(null);
        setCompletionComment("");
    }

    async function changeStatus(task) {
        if (!task.completed) {
            clearPhotos("completion");
            setCompletionComment("");
            setCompletionTask(task);
            return;
        }

        if (!window.confirm("Aufgabe wieder öffnen?")) return;

        setSaving(true);

        try {
            await ToDoApi.reopenTask(task.id);
            await reloadTasks();

        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    }

    async function completeTask() {
        if (
            !completionTask ||
            !completionPhotos.length ||
            saving ||
            photoProcessing
        ) return;

        setSaving(true);

        try {
            await ToDoApi.completeTask(
                completionTask.id,
                completionPhotos,
                completionComment.trim()
            );

            clearPhotos("completion");
            setCompletionTask(null);
            setCompletionComment("");

            await reloadTasks();

        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    }

    // Удаление задания
    async function deleteTask(task) {
        if (!window.confirm("Aufgabe und alle Fotos löschen?")) {
            return;
        }

        setSaving(true);

        try {
            await ToDoApi.deleteTask(task.id);
            await reloadTasks();

        } catch (err) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <MainDiv>
            <Header>
                <Title>Aufgaben</Title>

                <Button
                    type="button"
                    disabled={saving}
                    onClick={openNewTask}
                >
                    + Neue Aufgabe
                </Button>
            </Header>

            <TaskFilters
                houses={houses}
                Floors={Floors}
                selectedHouse={selectedHouse}
                setSelectedHouse={setSelectedHouse}
                selectedFloor={selectedFloor}
                setSelectedFloor={setSelectedFloor}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
            />

            <TaskList
                tasks={tasks}
                loading={loading}
                error={error}
                busy={saving}
                onPreview={setPreviewPhoto}
                onStatusChange={changeStatus}
                onDelete={deleteTask}
                onOpen={setSelectedTask}
            />

            {modalOpen && (
                <NewTaskModal
                    houses={houses}
                    Floors={Floors}
                    form={{
                        ...form,
                        photos: createPhotos
                    }}
                    setForm={setForm}
                    saving={saving}
                    photoProcessing={photoProcessing}
                    onClose={closeNewTask}
                    onSave={saveTask}
                    onPhotos={handleFiles}
                    onOpenCamera={openCamera}
                    onRemovePhoto={removePhoto}
                    onPreview={setPreviewPhoto}
                />
            )}

            {completionTask && (
                <CompleteTaskModal
                    task={completionTask}
                    photos={completionPhotos}
                    comment={completionComment}
                    setComment={setCompletionComment}
                    saving={saving}
                    photoProcessing={photoProcessing}
                    onClose={closeCompletion}
                    onComplete={completeTask}
                    onPhotos={handleFiles}
                    onOpenCamera={openCamera}
                    onRemovePhoto={removePhoto}
                    onPreview={setPreviewPhoto}
                />
            )}

            {cameraOpen && (
                <Camera
                    setPicture={receiveCameraPhoto}
                    setCameraOpen={setCameraOpen}
                />
            )}

            <PhotoLightbox
                src={previewPhoto}
                onClose={() => setPreviewPhoto(null)}
            />
            <TaskDetailsModal
                task={selectedTask}
                busy={saving}
                onClose={() => setSelectedTask(null)}
                onComplete={changeStatus}
                onDelete={async task => {
                    await deleteTask(task);
                    setSelectedTask(null);
                }}
                onPreview={setPreviewPhoto}
            />
        </MainDiv>
    );
};

export default ToDo;
